/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { PlacementFrontend } from '@post-office/browser--growth/confluence-side-nav';
import { AdControls } from '@post-office/components--ad-controls';
import { useMessageEvent } from '@post-office/message-lifecycle';
import {
	type AnyFrontendStage,
	type FrontendPlacementMessage,
} from '@post-office/message-maker-browser/types';
import { type CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';
import { FormattedMessage, useIntl } from 'react-intl';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../..';
import { registeredContent } from './content';

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		width: '100%',
	},
});

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({ cohort, jiraUrl, eligibleAsOf, handleOnDismissed }: MessageFrontendProps) => {
			const intl = useIntl();
			const { messageInstanceClicked } = useMessageInstanceEvent();
			const { messageDismissed } = useMessageEvent();

			const isAssignedWork = cohort === 'assigned-work';
			const title = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkTitle
					: registeredContent.default.activeTeamTitle,
			);
			const body = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkBody
					: registeredContent.default.activeTeamBody,
			);
			const cta = intl.formatMessage(
				isAssignedWork
					? registeredContent.default.assignedWorkCta
					: registeredContent.default.activeTeamCta,
			);
			const closeLabel = intl.formatMessage(registeredContent.default.close);

			const dismiss = (clickIdentifier: 'c2j-ad-controls-dismiss' | 'c2j-close-dismiss') => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier,
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: { actionType: 'dismiss' },
					additionalMetadataFromFrontend: { cohort, eligibleAsOf },
				});
				void messageDismissed();
				handleOnDismissed();
			};

			const handleCtaClicked = () => {
				void messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'c2j-open-jira-cta',
						clickedElement: 'button',
						buttonType: 'primary',
					},
					action: { actionType: 'open' },
					additionalMetadataFromFrontend: { cohort, eligibleAsOf },
				});
				void messageDismissed();
				handleOnDismissed();
			};

			return (
				<AnalyticsContext data={{ source: 'c2j-confluence-side-nav', cohort }}>
					<Stack css={styles.container} space="space.150">
						<Inline spread="space-between" alignBlock="start">
							<Heading size="small">{title}</Heading>
							<Inline>
								<AdControls
									iconSize="small"
									showWhyAmISeeingThis={true}
									whyAmISeeingThisModalContent={{
										title: <FormattedMessage {...registeredContent.default.whyTitle} />,
										message: <FormattedMessage {...registeredContent.default.whyBody} />,
										reasons: [],
									}}
									onDismissed={() => dismiss('c2j-ad-controls-dismiss')}
								/>
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={closeLabel}
									onClick={() => dismiss('c2j-close-dismiss')}
								/>
							</Inline>
						</Inline>
						<span>{body}</span>
						<LinkButton href={jiraUrl} onClick={handleCtaClicked} spacing="compact">
							{cta}
						</LinkButton>
					</Stack>
				</AnalyticsContext>
			);
		},
	},
});
